import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Share2, Search, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SharedAnalysesPanel — admin-only.
 *
 * Lists every saved spreadsheet record across all users with a tick-box.
 * Ticking flips is_shared=true on the record so non-admin users can read
 * it via the existing GET /spreadsheet/records endpoints. Replaces the
 * legacy "name contains 'example' → public" hack with an explicit flag.
 */
export default function SharedAnalysesPanel({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [pendingId, setPendingId] = useState(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/spreadsheet-records`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(res.data.records || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load saved analyses');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const toggleShare = async (id, nextValue) => {
    setPendingId(id);
    // Optimistic update for snappy UX — revert on error.
    setRecords(prev => prev.map(r => r.id === id ? { ...r, is_shared: nextValue } : r));
    try {
      await axios.patch(
        `${API}/admin/spreadsheet-records/${id}/share`,
        { is_shared: nextValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(nextValue ? 'Record shared with all users' : 'Sharing disabled');
    } catch (e) {
      // Revert
      setRecords(prev => prev.map(r => r.id === id ? { ...r, is_shared: !nextValue } : r));
      toast.error(e.response?.data?.detail || 'Failed to update sharing');
    } finally {
      setPendingId(null);
    }
  };

  const filtered = filter
    ? records.filter(r =>
        (r.name || '').toLowerCase().includes(filter.toLowerCase()) ||
        (r.owner_email || '').toLowerCase().includes(filter.toLowerCase()) ||
        (r.species || '').toLowerCase().includes(filter.toLowerCase()) ||
        (r.module || '').toLowerCase().includes(filter.toLowerCase())
      )
    : records;

  const sharedCount = records.filter(r => r.is_shared).length;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="shared-analyses-panel">
      <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-cyan-50 to-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Share2 className="h-5 w-5 text-cyan-700" />
            Shared Analyses
            <Badge
              variant="outline"
              className="ml-2 border-cyan-300 bg-cyan-50 text-cyan-700 font-normal"
            >
              {sharedCount} shared · {records.length} total
            </Badge>
          </CardTitle>
          <span className="text-xs text-slate-500">
            Tick to make a record visible to all users (read-only).
          </span>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="shared-analyses-filter"
            placeholder="Filter by name, owner, or species…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-9"
          />
        </div>

        {loading ? (
          <p className="text-sm text-slate-400 py-4 text-center">Loading saved analyses…</p>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-sm">
            <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 opacity-40" />
            {records.length === 0 ? 'No saved analyses yet.' : 'No matches for that filter.'}
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left w-16">Share</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left hidden sm:table-cell">Module</th>
                  <th className="px-3 py-2 text-left hidden md:table-cell">Owner</th>
                  <th className="px-3 py-2 text-left hidden md:table-cell">Species</th>
                  <th className="px-3 py-2 text-left hidden lg:table-cell">Updated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-slate-100 hover:bg-slate-50 transition-colors"
                    data-testid={`shared-record-row-${r.id}`}
                  >
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={!!r.is_shared}
                        disabled={pendingId === r.id}
                        onCheckedChange={(v) => toggleShare(r.id, !!v)}
                        data-testid={`shared-record-toggle-${r.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-800">
                      {r.name}
                      {r.is_shared && (
                        <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200" variant="outline">
                          Shared
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 hidden sm:table-cell">
                      <Badge variant="outline" className="border-cyan-200 bg-cyan-50 text-cyan-700 font-normal">
                        {r.module || 'Spreadsheet Comparison'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell text-slate-600">{r.owner_email}</td>
                    <td className="px-3 py-2 hidden md:table-cell text-slate-600">{r.species}</td>
                    <td className="px-3 py-2 hidden lg:table-cell text-slate-500 text-xs">
                      {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-slate-500 leading-relaxed">
          Shared records replace the previous &lsquo;name contains <em>example</em>&rsquo; convention.
          When ticked, the record appears at the bottom of every user&apos;s
          Spreadsheet Comparison record list as a read-only reference. Untick
          to make it private again.
        </p>
      </CardContent>
    </Card>
  );
}
